import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

/**
 * Cores dos gráficos vindas dos tokens.
 *
 * Recharts e o Google Maps recebem cor por prop em JS, não por classe —
 * então não alcançam a folha de estilo. Este hook lê os tokens já
 * resolvidos no elemento do tema e devolve hex/rgb prontos, refazendo a
 * leitura quando o usuário troca de tema.
 */
export interface ChartTheme {
  grid: string;
  axis: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  primary: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  muted: string;
  /** Paleta categórica, na ordem — para séries sem cor semântica. */
  series: string[];
}

const TOKENS: Record<keyof Omit<ChartTheme, 'series'>, string> = {
  grid: '--border',
  axis: '--muted',
  tooltipBg: '--surface-2',
  tooltipBorder: '--border',
  tooltipText: '--text',
  primary: '--primary',
  success: '--success',
  warning: '--warning',
  danger: '--danger',
  info: '--info',
  muted: '--muted',
};

/** Fallback do tema escuro, usado antes do primeiro paint. */
const FALLBACK: ChartTheme = {
  grid: '#2b3039',
  axis: '#7c848f',
  tooltipBg: '#1f232a',
  tooltipBorder: '#2b3039',
  tooltipText: '#edeff3',
  primary: '#7b95f5',
  success: '#4fbf8b',
  warning: '#e0a63c',
  danger: '#f0806f',
  info: '#6fb0e8',
  muted: '#7c848f',
  series: ['#7b95f5', '#4fbf8b', '#e0a63c', '#f0806f', '#6fb0e8'],
};

function read(): ChartTheme {
  if (typeof document === 'undefined') return FALLBACK;
  const host = document.querySelector('.dark, .light') ?? document.documentElement;
  const cs = getComputedStyle(host);
  const out = {} as ChartTheme;
  (Object.keys(TOKENS) as Array<keyof typeof TOKENS>).forEach((key) => {
    out[key] = cs.getPropertyValue(TOKENS[key]).trim() || FALLBACK[key];
  });
  out.series = [out.primary, out.success, out.warning, out.danger, out.info];
  return out;
}

export function useChartTheme(): ChartTheme {
  const { resolved } = useTheme();
  const [theme, setTheme] = useState<ChartTheme>(read);

  useEffect(() => {
    // Um frame depois pra ler já com a classe do tema aplicada no wrapper.
    const id = requestAnimationFrame(() => setTheme(read()));
    return () => cancelAnimationFrame(id);
  }, [resolved]);

  return theme;
}
